import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { StudentService } from '../../services/student.service';
import { ExpenseService } from '../../services/expense.service';
import { NotificationService } from '../../services/notification.service';
import { StudentDetailPageActions, StudentFinancialApiActions, StudentFinancialPageActions, StudentHistoryApiActions, StudentRelatedApiActions, StudentRelatedMutationResult, StudentsApiActions, StudentsPageActions } from './students.actions';

@Injectable()
export class StudentsEffects {
  private readonly actions$ = inject(Actions);
  private readonly studentService = inject(StudentService);
  private readonly expenseService = inject(ExpenseService);
  private readonly notificationService = inject(NotificationService);

  readonly loadStudents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StudentsPageActions.opened, StudentsPageActions.filtersChanged),
      switchMap(() => this.studentService.getAll().pipe(
        map((students) => StudentsApiActions.loadSucceeded({ students })),
        catchError((error: { message?: string }) => of(
          StudentsApiActions.loadFailed({ error: error?.message ?? 'Unable to load students' }),
        )),
      )),
    ),
  );

  readonly notifyLoadFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(StudentsApiActions.loadFailed),
      tap(() => this.notificationService.error('STUDENTS.ERROR_LOADING')),
    ),
    { dispatch: false },
  );

  readonly loadStudentDetail$ = createEffect(() => this.actions$.pipe(
    ofType(StudentDetailPageActions.opened),
    switchMap(({ studentId }) => this.studentService.getById(studentId).pipe(
      map((student) => StudentsApiActions.detailLoadSucceeded({ student })),
      catchError((error: { message?: string }) => of(StudentsApiActions.detailLoadFailed({ error: error?.message ?? 'Unable to load student' }))),
    )),
  ));

  readonly updateStudent$ = createEffect(() => this.actions$.pipe(
    ofType(StudentDetailPageActions.updateRequested),
    switchMap(({ requestId, request }) => this.studentService.update(request).pipe(
      map((student) => StudentsApiActions.updateSucceeded({ requestId, student })),
      catchError((error: { message?: string }) => of(StudentsApiActions.updateFailed({ requestId, error: error?.message ?? 'Unable to update student' }))),
    )),
  ));

  readonly saveStudentHistory$ = createEffect(() => this.actions$.pipe(
    ofType(StudentDetailPageActions.historySaveRequested),
    switchMap(({ studentId, original, request }) => this.studentService.saveChangeRecord(studentId, original, request).pipe(
      map((history) => StudentHistoryApiActions.saveSucceeded({ studentId, history })),
      catchError((error: { message?: string }) => of(StudentHistoryApiActions.saveFailed({ studentId, error: error?.message ?? 'Unable to save student history' }))),
    )),
  ));

  readonly loadStudentFinancial$ = createEffect(() => this.actions$.pipe(
    ofType(StudentFinancialPageActions.opened),
    switchMap(({ studentId }) => forkJoin({
      expenses: this.expenseService.getByStudent(studentId),
      donations: this.expenseService.getDonationsByStudent(studentId),
    }).pipe(
      map(({ expenses, donations }) => StudentFinancialApiActions.loadSucceeded({ studentId, expenses, donations })),
      catchError((error: { message?: string }) => of(StudentFinancialApiActions.loadFailed({ studentId, error: error?.message ?? 'Unable to load student finances' }))),
    )),
  ));

  readonly loadStudentRelated$ = createEffect(() => this.actions$.pipe(
    ofType(StudentDetailPageActions.relatedOpened),
    switchMap(({ studentId, universityId }) => this.studentService.getUniversities(studentId).pipe(
      switchMap((universities) => forkJoin({
        changeHistory: this.studentService.getChangeHistory(studentId),
        universities: of(universities),
        academicHistory: (universityId ?? universities[0]?.id)
          ? this.studentService.getAcademicHistory(studentId, universityId ?? universities[0].id)
          : of([]),
        documents: this.studentService.getDocuments(studentId),
        notes: this.studentService.getNotes(studentId),
      })),
      map((related) => StudentsApiActions.relatedLoadSucceeded({ studentId, ...related })),
      catchError((error: { message?: string }) => of(StudentsApiActions.relatedLoadFailed({ studentId, error: error?.message ?? 'Unable to load student details' }))),
    )),
  ));

  readonly mutateStudentRelated$ = createEffect(() => this.actions$.pipe(
    ofType(StudentDetailPageActions.relatedMutationRequested),
    concatMap(({ requestId, mutation }) => {
      const request$: Observable<StudentRelatedMutationResult> = mutation.kind === 'addUniversity'
        ? this.studentService.addUniversity(mutation.studentId, mutation.association).pipe(map((items) => ({ kind: 'universities' as const, items })))
        : mutation.kind === 'removeUniversity'
          ? this.studentService.removeUniversity(mutation.studentId, mutation.associationId).pipe(map((items) => ({ kind: 'universities' as const, items })))
          : mutation.kind === 'addAcademicRecord'
            ? this.studentService.addAcademicRecord(mutation.studentId, mutation.universityId, mutation.record).pipe(map((items) => ({ kind: 'academicHistory' as const, items })))
            : mutation.kind === 'removeAcademicRecord'
              ? this.studentService.removeAcademicRecord(mutation.studentId, mutation.universityId, mutation.recordId).pipe(map((items) => ({ kind: 'academicHistory' as const, items })))
              : mutation.kind === 'uploadDocument'
                ? this.studentService.uploadDocument(mutation.studentId, mutation.name, mutation.file).pipe(map((items) => ({ kind: 'documents' as const, items })))
                : mutation.kind === 'removeDocument'
                  ? this.studentService.removeDocument(mutation.studentId, mutation.documentId).pipe(map((items) => ({ kind: 'documents' as const, items })))
                  : mutation.kind === 'addNote'
                    ? this.studentService.addNote(mutation.studentId, mutation.content, mutation.createdBy).pipe(map((items) => ({ kind: 'notes' as const, items })))
                    : this.studentService.removeNote(mutation.studentId, mutation.noteId).pipe(map((items) => ({ kind: 'notes' as const, items })));
      return request$.pipe(
        map((result) => StudentRelatedApiActions.mutationSucceeded({ requestId, result })),
        catchError((error: { message?: string }) => of(StudentRelatedApiActions.mutationFailed({ requestId, error: error?.message ?? 'Unable to update student details' }))),
      );
    }),
  ));
}